import React, { useEffect, useMemo, useState } from 'react';
import DisplayPage from '../components/DisplayPage';
import type { PageModel as ApiPageModel } from '../lib/pages';
import { getPage } from '../lib/pages';

const cls = {
  container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
};
const PREVIEW_CANVAS_WIDTH = 1024; // match editor canvas

const PagePreview: React.FC = () => {
  const [page, setPage] = useState<ApiPageModel | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // Per-section measurement to compute zoom that fits width
  const sectionRefs = React.useRef<Record<number, HTMLDivElement | null>>({});
  const [containerWByIndex, setContainerWByIndex] = useState<Record<number, number>>({});
  // Track scaled heights so the background container matches the last row exactly
  const innerScaledRefs = React.useRef<Record<number, HTMLDivElement | null>>({});
  const [scaledHByIndex, setScaledHByIndex] = useState<Record<number, number>>({});

  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  useEffect(() => {
    (async () => {
      const id = params.get('id');
      const slug = params.get('slug');
      let loaded: ApiPageModel | null = null;
      if (id) {
        try {
          const key = `page_preview_${id}`;
          const raw = localStorage.getItem(key);
          if (raw) {
            const data = JSON.parse(raw);
            loaded = data.page as ApiPageModel;
          }
        } catch {}
      }
      if (!loaded && slug) {
        const p = await getPage(slug);
        if (p) loaded = p as ApiPageModel;
      }
      setPage(loaded);
      setLoading(false);
    })();
  }, [params]);

  const sections = useMemo(() => {
    const anyPage: any = page as any;
    if (!anyPage) return [] as ApiPageModel[];
    if (Array.isArray(anyPage.sections)) return (anyPage.sections as any[]) || [];
    const { title, rows, columns, gapX, gapY, radius, colFractions, rowFractions, merges, blocks, rowGaps, colGaps, layoutHeight, rowHeightsPx, padByCell, keepRadiusByCell, customRadiusByCell, customRadiusEnabledByCell, cellEdgeRadiusByCell, cellEdgeRadiusEnabledByCell, sectionType, headingByCell, buttonByCell } = anyPage;
    return [{ title, rows, columns, gapX, gapY, radius, colFractions, rowFractions, merges, blocks, rowGaps, colGaps, layoutHeight, rowHeightsPx, padByCell, keepRadiusByCell, customRadiusByCell, customRadiusEnabledByCell, cellEdgeRadiusByCell, cellEdgeRadiusEnabledByCell, headingByCell, buttonByCell, sectionType: sectionType || 'content' }];
  }, [page]);

  useEffect(() => {
    const update = () => {
      const next: Record<number, number> = {};
      const entries = Object.entries(sectionRefs.current);
      for (const [k, el] of entries) {
        if (!el) continue;
        const rectW = el.getBoundingClientRect().width || el.clientWidth || 0;
        next[Number(k)] = Math.max(0, Math.ceil(rectW));
      }
      setContainerWByIndex(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      const nextH: Record<number, number> = {};
      const entriesH = Object.entries(innerScaledRefs.current);
      for (const [k, el] of entriesH) {
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        nextH[Number(k)] = Math.max(0, Math.ceil(rect.height) + 1);
      }
      setScaledHByIndex(prev => (JSON.stringify(prev) === JSON.stringify(nextH) ? prev : nextH));
    };
    const id = window.requestAnimationFrame(update);
    return () => window.cancelAnimationFrame(id);
  }, [sections]);

  // Observe element resizes so scaling/height initialize correctly without requiring scroll
  useEffect(() => {
    const update = () => {
      const next: Record<number, number> = {};
      for (const [k, el] of Object.entries(sectionRefs.current)) {
        if (!el) continue;
        const rectW = el.getBoundingClientRect().width || el.clientWidth || 0;
        next[Number(k)] = Math.max(0, Math.ceil(rectW));
      }
      setContainerWByIndex(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      const nextH: Record<number, number> = {};
      for (const [k, el] of Object.entries(innerScaledRefs.current)) {
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        nextH[Number(k)] = Math.max(0, Math.ceil(rect.height) + 1);
      }
      setScaledHByIndex(prev => (JSON.stringify(prev) === JSON.stringify(nextH) ? prev : nextH));
    };
    let ro: ResizeObserver | null = null;
    try {
      if (typeof window !== 'undefined' && (window as any).ResizeObserver) {
        ro = new (window as any).ResizeObserver(() => update());
        Object.values(sectionRefs.current).forEach(el => { if (el) ro!.observe(el); });
        Object.values(innerScaledRefs.current).forEach(el => { if (el) ro!.observe(el); });
      }
    } catch {}
    update();
    window.addEventListener('load', update);
    return () => {
      window.removeEventListener('load', update);
      if (ro) ro.disconnect();
    };
  }, [sections]);

  // Measure widths on mount/resize/scroll
  useEffect(() => {
    const update = () => {
      const next: Record<number, number> = {};
      const entries = Object.entries(sectionRefs.current);
      for (const [k, el] of entries) {
        if (!el) continue;
        const rectW = el.getBoundingClientRect().width || el.clientWidth || 0;
        next[Number(k)] = Math.max(0, Math.ceil(rectW));
      }
      setContainerWByIndex(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      // also measure scaled heights so containers match content
      const nextH: Record<number, number> = {};
      const entriesH = Object.entries(innerScaledRefs.current);
      for (const [k, el] of entriesH) {
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        nextH[Number(k)] = Math.max(0, Math.round(rect.height));
      }
      setScaledHByIndex(prev => (JSON.stringify(prev) === JSON.stringify(nextH) ? prev : nextH));
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, []);

  return (
    <div className="min-h-screen page-preview">
      <main className={`${cls.container} py-6`}>
        <style>{`
          /* Hide editor-only hover outlines and gear in preview */
          .page-preview [style*="rgba(59,130,246"] { border-color: transparent !important; border-width: 0 !important; box-shadow: none !important; }
          .page-preview [style*="border: 1px"] { border-color: transparent !important; }
          .page-preview button[title="Settings"],
          .page-preview [title="Settings"] { display: none !important; }
          .page-preview * { outline: none !important; }
        `}</style>
        {/* Removed global user-select and pointer-events disabling to allow selecting text and clicking buttons in preview */}
        {loading && (
          <div className="relative rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <div className="pointer-events-none inset-0 flex items-center justify-center rounded-2xl">
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="relative inline-flex h-6 w-6">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-6 w-6 bg-gradient-to-br from-purple-600 to-blue-600 animate-spin [animation-duration:1200ms]" />
                </span>
                Preparing preview…
              </div>
            </div>
          </div>
        )}
        {!loading && !page && (
          <div className="relative rounded-2xl border border-gray-200 bg-white shadow-sm p-6 text-sm text-gray-600">No preview data found.</div>
        )}
        {page && (
          <div>
            {sections.map((sec, i) => {
              const s: any = sec as any;
              const isHero = (s.sectionType === 'heroFooter');
              const rowsCount = (Array.isArray(s.rowFractions) && s.rowFractions.length) ? s.rowFractions.length : (s.rows || 1);
              const needGaps = Math.max(0, rowsCount - 1);
              const rowGapsArr: number[] = (Array.isArray(s.rowGaps) && s.rowGaps.length === needGaps)
                ? s.rowGaps
                : Array.from({ length: needGaps }, () => s.gapY ?? 16);
              const gapsSum = rowGapsArr.reduce((a,b)=>a+(b||0),0);
              const fallbackH = (typeof s.layoutHeight === 'number' ? s.layoutHeight : 0) + gapsSum;
              const targetH = Math.max(0, Math.round(s.targetHeightPx || fallbackH || 0));
              if (isHero) {
                const winW = (document.documentElement?.clientWidth || window.innerWidth || 0);
                const cw = winW > 0 ? winW : (containerWByIndex[i] || 0);
                const cwAdj = Math.max(0, cw - 2);
                const zoom = cwAdj > 0 ? Math.max(0.25, Math.min(3, cwAdj / PREVIEW_CANVAS_WIDTH)) : 1;
                return (
                  <div key={i} className={i>0 ? 'mt-10' : ''} style={{ marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }}>
                    <div ref={el => { sectionRefs.current[i] = el; }} style={{ width: `${cwAdj}px`, height: targetH ? `${Math.round(targetH * zoom)}px` : undefined, overflow: 'hidden' }}>
                      <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: `${PREVIEW_CANVAS_WIDTH}px`, height: targetH ? `${targetH}px` : undefined }}>
                        <DisplayPage page={s} editable={false} compact showInnerHelpers={false} showGapHandles={false} />
                      </div>
                    </div>
                  </div>
                );
              }
              {
                const isFooter = (s.sectionType === 'footer');
                const cw = containerWByIndex[i] || 0;
                // Subtract 1px to avoid right-edge clipping from subpixel rounding when overflow hidden
                const safeW = cw > 1 ? (cw - 1) : cw;
                const zoom = safeW > 0 ? Math.max(0.25, Math.min(3, safeW / PREVIEW_CANVAS_WIDTH)) : 1;
                // For content sections, allow natural height (no overflow clipping) so nothing gets cut off.
                // Also use full available width so it scales to the right border with correct spacing.
                return (
                  <div key={i} className={`relative rounded-2xl border border-gray-200 bg-white shadow-sm p-6 ${i>0 ? 'mt-10' : ''}`}>
                    <div ref={el => { sectionRefs.current[i] = el; }} style={{ width: '100%', height: scaledHByIndex[i] ? `${scaledHByIndex[i] + 6 + (isFooter ? 4 : 0)}px` : undefined }}>
                      <div ref={el => { innerScaledRefs.current[i] = el; }} style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: `${PREVIEW_CANVAS_WIDTH}px` }}>
                        <DisplayPage page={s} editable={false} compact showInnerHelpers={false} showGapHandles={false} />
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default PagePreview;
